const API_URL = process.env.REACT_APP_API_BASE_URL || '';

/**
 * Daycare API Service
 * Handles all daycare-related API operations (packages, visits)
 */
export class DaycareService {
    
    // ========== PACKAGE OPERATIONS ==========
    
    /**
     * Create a new daycare package
     * @param {Object} packageData - Package data { dog, packageType }
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<Object>} Created package
     */
    static async createPackage(packageData, authorizedFetch) {
        const response = await authorizedFetch(`${API_URL}/api/daycare/packages`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(packageData)
        });
        
        if (!response.ok) {
            throw new Error(await response.text());
        }
        
        return response.json();
    }

    /**
     * Get all active daycare packages
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<Array>} Array of active packages
     */
    static async getActivePackages(authorizedFetch) {
        const response = await authorizedFetch(`${API_URL}/api/daycare/packages/active`);
        
        if (!response.ok) {
            throw new Error(await response.text());
        }
        
        return response.json();
    }

    /**
     * Update a daycare package
     * @param {string} packageId - Package ID
     * @param {Object} packageData - Updated package data
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<Object>} Updated package
     */
    static async updatePackage(packageId, packageData, authorizedFetch) {
        const response = await authorizedFetch(`${API_URL}/api/daycare/packages/${packageId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(packageData)
        });
        
        if (!response.ok) {
            throw new Error(await response.text());
        }
        
        return response.json();
    }

    /**
     * Delete a daycare package
     * @param {string} packageId - Package ID
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<void>}
     */
    static async deletePackage(packageId, authorizedFetch) {
        const response = await authorizedFetch(`${API_URL}/api/daycare/packages/${packageId}`, {
            method: 'DELETE'
        });
        
        if (!response.ok) {
            throw new Error(await response.text());
        }
    }

    static async cancelPackage(packageId, authorizedFetch, note = '') {
        const response = await authorizedFetch(`${API_URL}/api/daycare/packages/${packageId}/cancel`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note }),
        });
        if (!response.ok) throw new Error(await response.text());
    }

    static async getPackageHistory(authorizedFetch) {
        const response = await authorizedFetch(`${API_URL}/api/daycare/packages/history`);
        if (!response.ok) throw new Error(await response.text());
        return response.json();
    }

    // ========== VISIT OPERATIONS ==========

    /**
     * Create a new daycare visit (check-in)
     * @param {Object} visitData - Visit data { dog, date, checkInTime, notes }
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<Object>} Created visit
     */
    static async createVisit(visitData, authorizedFetch) {
        const response = await authorizedFetch(`${API_URL}/api/daycare/visits`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(visitData)
        });
        
        if (!response.ok) {
            throw new Error(await response.text());
        }
        
        return response.json();
    }

    /**
     * Update a daycare visit (check-out)
     * @param {string} visitId - Visit ID
     * @param {Object} visitData - Updated visit data { checkOutTime, notes }
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<Object>} Updated visit
     */
    static async updateVisit(visitId, visitData, authorizedFetch) {
        const response = await authorizedFetch(`${API_URL}/api/daycare/visits/${visitId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(visitData)
        });
        
        if (!response.ok) {
            throw new Error(await response.text());
        }
        
        return response.json();
    }

    /**
     * Get today's daycare visits
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<Array>} Array of today's visits
     */
    static async getTodaysVisits(authorizedFetch, date = null) {
        const d = new Date();
        const localToday = [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
        const qs = `?date=${date || localToday}`;
        const response = await authorizedFetch(`${API_URL}/api/daycare/visits/today${qs}`);

        if (!response.ok) {
            throw new Error(await response.text());
        }

        return response.json();
    }

    /**
     * Get visits from previous days that were never checked out
     * @param {Function} authorizedFetch - Authenticated fetch function
     * @returns {Promise<Array>} Array of still-open visits, oldest first
     */
    static async getOpenPreviousVisits(authorizedFetch) {
        const response = await authorizedFetch(`${API_URL}/api/daycare/visits/open-previous`);

        if (!response.ok) {
            throw new Error(await response.text());
        }

        return response.json();
    }
}
